import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import './Profile.css';

const Profile = () => {
  const { user, dispatch, logout } = useAuth();
  const navigate = useNavigate();

  const [form, setForm] = useState({
    name: user?.name || '',
    phone: user?.phone || '',
    avatar: user?.avatar || ''
  });
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e) => {
    setForm(prev => ({ ...prev, [e.target.name]: e.target.value }));
    setSaved(false);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      const res = await axios.put('/api/users/profile', form);
      dispatch({ type: 'UPDATE_USER', payload: res.data.user });
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  return (
    <div className="profile-page">
      <Navbar />
      <div className="profile-container container">
        <div className="profile-header">
          <h1>My Profile</h1>
          <p>Manage your account details and preferences</p>
        </div>

        <div className="profile-layout">
          {/* Sidebar */}
          <aside className="profile-sidebar">
            <div className="profile-avatar-section">
              <div className="profile-avatar-large">
                {user?.name?.charAt(0).toUpperCase()}
              </div>
              <h3 className="profile-name">{user?.name}</h3>
              <p className="profile-email">{user?.email}</p>
              {user?.role === 'admin' && (
                <span className="badge badge-accent" style={{ marginTop: 8 }}>Admin</span>
              )}
            </div>
            <nav className="profile-nav">
              <button className="profile-nav-item active">
                <span><i class="fa-solid fa-user"></i></span> Personal Info
              </button>
              <button
                className="profile-nav-item"
                onClick={() => navigate('/my-bookings')}
              >
                <span><i class="fa-solid fa-calendar-days"></i></span> My Bookings
              </button>
              <button className="profile-nav-item logout-item" onClick={handleLogout}>
                <span><i class="fa-solid fa-arrow-right-from-bracket"></i></span> Sign Out
              </button>
            </nav>
          </aside>

          {/* Main */}
          <main className="profile-main">
            <div className="profile-card">
              <div className="profile-card-header">
                <h2>Personal Information</h2>
                <p>Update your profile details below</p>
              </div>

              {error && <div className="alert alert-error" style={{ marginBottom: 20 }}>{error}</div>}
              {saved && <div className="alert alert-success" style={{ marginBottom: 20 }}>✓ Profile updated successfully!</div>}

              <form onSubmit={handleSave}>
                <div className="profile-form-grid">
                  <div className="form-group">
                    <label className="form-label">Full Name</label>
                    <input
                      type="text"
                      name="name"
                      className="form-input"
                      value={form.name}
                      onChange={handleChange}
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label className="form-label">Email Address</label>
                    <input
                      type="email"
                      className="form-input"
                      value={user?.email}
                      disabled
                      style={{ opacity: 0.6, cursor: 'not-allowed' }}
                    />
                    <span className="field-note">Email cannot be changed</span>
                  </div>

                  <div className="form-group">
                    <label className="form-label">Phone Number</label>
                    <input
                      type="tel"
                      name="phone"
                      className="form-input"
                      value={form.phone}
                      onChange={handleChange}
                      placeholder="+1 555 000 0000"
                    />
                  </div>

                  <div className="form-group">
                    <label className="form-label">Avatar URL (optional)</label>
                    <input
                      type="url"
                      name="avatar"
                      className="form-input"
                      value={form.avatar}
                      onChange={handleChange}
                      placeholder="https://..."
                    />
                  </div>
                </div>

                <div className="profile-actions">
                  <button type="submit" className="btn btn-primary" disabled={saving}>
                    {saving ? 'Saving...' : 'Save Changes'}
                  </button>
                  <button
                    type="button"
                    className="btn btn-outline"
                    onClick={() => navigate('/my-bookings')}
                  >
                    View My Trips
                  </button>
                </div>
              </form>
            </div>

            {/* Account stats */}
            <div className="profile-stats-card">
              <div className="profile-stat">
                <span className="profile-stat-icon"><i class="fa-solid fa-earth-americas"></i></span>
                <div>
                  <p className="profile-stat-label">Member Since</p>
                  <p className="profile-stat-value">
                    {new Date(user?.createdAt || Date.now()).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
                  </p>
                </div>
              </div>
              <div className="profile-stat">
                <span className="profile-stat-icon"><i class="fa-solid fa-star"></i></span>
                <div>
                  <p className="profile-stat-label">Account Type</p>
                  <p className="profile-stat-value">{user?.role === 'admin' ? 'Administrator' : 'Traveller'}</p>
                </div>
              </div>
              <div className="profile-stat">
                <span className="profile-stat-icon"><i class="fa-solid fa-lock"></i></span>
                <div>
                  <p className="profile-stat-label">Account Status</p>
                  <p className="profile-stat-value" style={{ color: 'var(--green)' }}>Active & Verified</p>
                </div>
              </div>
            </div>
          </main>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default Profile;
